<template>
  <div class="mb-5">
    <label class="required fs-xl mt-0"> Sender Address </label>
    <CustomSelect
      :selected="senderAddressId"
      :options="senderAddresses"
      placeholder="Choose sender address"
      class="w-100"
      @selected="onSelect"
    >
      <template #custom_option="{ option }: { option: Address }">
        <div :class="'d-flex align-items-baseline item-' + option.id">
          <div class="d-flex justify-content-between w-100 align-items-center">
            <div class="d-flex align-items-baseline">
              <span class="status" />
              <span class="name d-block mr-2">{{ option.companyName || '' }} </span>
              <div>{{ option.countryCode }}, {{ option.city }}, {{ option.addressLine }}</div>
              <div v-if="isStaff" class="d-flex ml-2">
                <b-badge v-if="option.isDefault" class="ml-1 mt-0" variant="primary"> Default Address</b-badge>
                <b-badge v-if="option.hasCustomPrices" variant="success" class="ml-1"> Sender prices</b-badge>
                <b-badge v-if="option.hasCustomPriorities" variant="warning" class="ml-1"> Sender carriers</b-badge>
              </div>
            </div>
            <div>
              <b-button
                class="d-flex align-items-center justify-content-center action-btn"
                variant="outline-secondary"
                @click="openSenderAddressEditModal(option.id)"
              >
                <mdicon name="pencil-outline" class="pointer" size="18" />
              </b-button>
            </div>
          </div>
        </div>
      </template>
      <template #custom_list-footer>
        <div v-if="customerId" class="list-footer">
          <div class="create-client" @click="openModal(SENDER_ADDRESS_ACTION, { customerId: customerId, id: '' })">
            Create New Sender Address
          </div>
        </div>
      </template>
    </CustomSelect>
    <div v-if="error" class="text-danger">{{ error }}</div>
    <SenderAddressActionModal :id="SENDER_ADDRESS_ACTION" @refresh="reloadAddresses" />
  </div>
</template>

<script lang="ts">
import type { CustomerModalData } from '@/types/Components/Modals'
import type { AssignmentCreationData } from '@/types/Models/Assignment'
import type { Address } from '@/types/Models/Customer'
import { computed, defineComponent, watch, type ComputedRef } from 'vue'
import { SENDER_ADDRESS_ACTION } from '@/const/ModalTypes'
import { openModal } from '@/services/Modal'
import { useAccountStore } from '@/store/account'
import { useAssignmentFormStore } from '@/store/assignmentForm'
import { useCustomersSenderAddressesStore } from '@/store/customers/senderAddresses'
import CustomSelect from '@/views/Components/Elements/Form/CustomSelect.vue'
import SenderAddressActionModal from '@/views/Modals/SenderAddress/SenderAddressActionModal.vue'

export default defineComponent({
  name: 'SenderAddress',
  components: { SenderAddressActionModal, CustomSelect },
  setup() {
    const accountStore = useAccountStore()
    const assignmentFormStore = useAssignmentFormStore()
    const customersSenderAddressesStore = useCustomersSenderAddressesStore()

    const assignment: ComputedRef<AssignmentCreationData> = computed(() => assignmentFormStore.assignment)
    const customerId: ComputedRef<string> = computed(() => assignment.value.company_id || '')
    const senderAddressId: ComputedRef<string> = computed(() => assignment.value.sender_address_id || '')
    const senderAddresses: ComputedRef<Address[]> = computed(() => customersSenderAddressesStore.senderAddresses)
    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)
    const error: ComputedRef<string> = computed(() => assignmentFormStore.errors.sender_address_id)

    const onSelect = (senderAddressId: string) => assignmentFormStore.setFields({ sender_address_id: senderAddressId })

    const openSenderAddressEditModal = (addressId: string) => {
      openModal(SENDER_ADDRESS_ACTION, { id: addressId, customerId: customerId.value } as CustomerModalData)
    }

    const reloadAddresses = async () => {
      customersSenderAddressesStore.resetSenderAddresses()
      if (customerId.value) {
        await customersSenderAddressesStore.fetch(customerId.value)
      }
    }

    watch(
      () => customerId.value,
      async (value) => {
        customersSenderAddressesStore.resetSenderAddresses()
        if (value) {
          await customersSenderAddressesStore.fetch(value)
        }
      },
    )

    watch(
      () => senderAddresses.value,
      (list: Address[]) => {
        const address = list.find((el) => el.isDefault) || list[0]
        if (address) {
          onSelect(address.id)
        }
      },
    )

    return {
      error,
      isStaff,
      customerId,
      senderAddresses,
      senderAddressId,
      onSelect,
      openModal,
      reloadAddresses,
      openSenderAddressEditModal,
      SENDER_ADDRESS_ACTION,
    }
  },
})
</script>
